import type { IRequestRepository } from '../repositories/request.repository'
import type {
  Request,
  CreateRequestInput,
  ContactFormPayload,
  GetRequestsParams,
  RequestListResult,
} from '../entities/request.entity'

/**
 * Сервис заявок (requests).
 * Делегирует вызовы репозиторию, соответствующие эндпоинтам бэкенда:
 * GET /requests/, GET /requests/check, POST /requests/
 */
export class RequestService {
  constructor(private readonly requestRepository: IRequestRepository) {}

  /**
   * Получить список заявок с пагинацией и фильтрами.
   */
  async getRequests(params?: GetRequestsParams): Promise<RequestListResult> {
    return this.requestRepository.findMany(params)
  }

  /**
   * Проверить, есть ли заявка с указанным телефоном или email.
   * @param phone — телефон у форматі лише цифр (наприклад 380634050489)
   * @param email — email (обязателен, если не передан phone)
   * @throws Error если не передан ни phone, ни email
   */
  async checkRequestExists(phone?: string, email?: string): Promise<boolean> {
    const phoneDigits = phone != null ? phone.replace(/\D/g, '').trim() : undefined
    return this.requestRepository.checkExists(phoneDigits, email)
  }

  /**
   * Создать новую заявку (сырой контракт API).
   */
  async createRequest(body: CreateRequestInput): Promise<Request> {
    return this.requestRepository.create(body)
  }

  /**
   * Призначити курс для заявки (двоетапний онбординг — студент обирає курс за посиланням).
   */
  async selectCourse(requestId: string, courseId: string): Promise<{ ok: boolean; aiEnabled: boolean }> {
    return this.requestRepository.selectCourse(requestId, courseId)
  }

  /**
   * Создать заявку из данных формы контакта.
   * Адаптация payload → API выполняется здесь, компонент не знает контракт бэкенда.
   * @param requestSource — origin сайта, на котором загружен UI (напр. https://landing.example.com)
   */
  async createRequestFromContactForm(
    payload: ContactFormPayload,
    accountId: string,
    requestSource?: string
  ): Promise<Request> {
    const body = this.toCreateRequestInput(payload, accountId, requestSource)
    return this.requestRepository.create(body)
  }

  private toCreateRequestInput(
    payload: ContactFormPayload,
    accountId: string,
    requestSource?: string
  ): CreateRequestInput {
    // API приймає номер тільки цифрами (наприклад 380634050489), без префікса UA та без "+"
    const phone = (payload.phone ?? '').replace(/\D/g, '').trim()
    return {
      request_user_name: payload.fullName.trim(),
      request_user_phone: phone,
      request_user_email: payload.email?.trim() || undefined,
      request_user_region_id: payload.region?.trim() || undefined,
      request_user_region_locality: payload.locality?.trim() || undefined,
      request_user_notes: payload.message?.trim() || undefined,
      request_account_id: accountId,
      request_course_id: payload.courseId?.trim() || undefined,
      request_source: requestSource?.trim() || 'website',
      request_utm_source:   payload.utmSource   || undefined,
      request_utm_medium:   payload.utmMedium   || undefined,
      request_utm_campaign: payload.utmCampaign || undefined,
      request_utm_content:  payload.utmContent  || undefined,
      request_utm_term:     payload.utmTerm     || undefined,
      request_referrer:     payload.referrer    || undefined,
      request_raw_url:      payload.rawUrl      || undefined,
    }
  }
}
